import React, { useContext, useMemo, useRef, useState } from 'react';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import { useIntl } from 'react-intl';
import translations from './fordeling.i18n';
import { format } from 'date-fns';
import { download, fetchStringContent } from '../../util/download';
import useSetCustomCursor from '../../hooks/useSetCustomCursor';
import Button from '../../components/button/button';
import { ConfirmationDialog } from '../../components/dialog';

interface PraksisstedDownloadButtons {
  delIdentitetsnummer?: boolean;
  isStudiestedbruker: false;
}

interface StudiestedDownloadButtons {
  delIdentitetsnummer?: never;
  isStudiestedbruker: true;
}

type DelIdentitetsnummerIsStudiested =
  | PraksisstedDownloadButtons
  | StudiestedDownloadButtons;

type FordelingDownloadButtonsProps = {
  isAdmin: boolean;
  brukerId: string;
  aktivitetId: string;
} & DelIdentitetsnummerIsStudiested;

const FordelingDownloadButtons: React.FC<
  React.PropsWithChildren<FordelingDownloadButtonsProps>
> = ({
  isAdmin,
  brukerId,
  aktivitetId,
  delIdentitetsnummer,
  isStudiestedbruker,
}) => {
  const { addErrorNotification } = useContext(NotificationFunctionContext);
  const [confirmDownloadDialogOpen, setConfirmDownloadDialogOpen] =
    useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const btnWrapperIkkePersonnrRef = useRef<HTMLDivElement>(null);
  const btnWrapperMedPersonnrRef = useRef<HTMLDivElement>(null);

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      lastNedStudenterPaaPraksisstedSomCsv: formatMessage({
        id: translations.lastNedStudenterPaaPraksisstedSomCsv.id,
      }),
      lastNedStudenterPaaPraksisstedSomCsvMedIdentitetsnummer: formatMessage({
        id: translations.lastNedStudenterPaaPraksisstedSomCsvMedIdentitetsnummer
          .id,
      }),
    }),
    [formatMessage],
  );

  const onClose = (submit: boolean, includeIdentitetsnummer: boolean) => {
    setConfirmDownloadDialogOpen(false);

    if (!submit) return;

    setLoadingCsv(true);

    const now = format(Date.now(), 'ddMMyyyy_HHmmss');

    const praksisstedURL = `/praksisstedapi/praksisaktivitet/${aktivitetId}/fordeling/csvdownload?skipHeaderline=false&includeIdentitetsnummer=${
      includeIdentitetsnummer ? 'true' : 'false'
    }`;

    const studiestedURL = `/studiestedapi/praksisaktivitet/${aktivitetId}/fordeling/download?skipHeaderline=false&includeIdentitetsnummer=${
      includeIdentitetsnummer ? 'true' : 'false'
    }`;

    fetchStringContent(isStudiestedbruker ? studiestedURL : praksisstedURL)
      .then((fileString) =>
        download({
          filename: `studenter_ved_praksissteder_${
            isStudiestedbruker ? 'studie' : 'praksis'
          }stedbruker_${brukerId}_${now}.csv`,
          fileType: 'text/csv',
          fileString,
        }),
      )
      .catch((err: Error) => addErrorNotification(err.message))
      .finally(() => setLoadingCsv(false));
  };

  const onCloseConfirmDownloadStudentsCsvDialog = (confirmed: boolean) => {
    setConfirmDownloadDialogOpen(false);
    if (confirmed) onClose(true, true);
  };

  useSetCustomCursor({
    cursorType: 'wait',
    shouldSetCursor: loadingCsv,
    ref: btnWrapperMedPersonnrRef,
  });

  useSetCustomCursor({
    cursorType: 'wait',
    shouldSetCursor: loadingCsv,
    ref: btnWrapperIkkePersonnrRef,
  });

  return (
    <>
      {isAdmin && (delIdentitetsnummer || isStudiestedbruker) && (
        <div ref={btnWrapperMedPersonnrRef}>
          <Button.Secondary
            disabled={loadingCsv}
            onClick={() => setConfirmDownloadDialogOpen(true)}
            data-cy={'download-assignment-with-number'}
          >
            {labels.lastNedStudenterPaaPraksisstedSomCsvMedIdentitetsnummer}
          </Button.Secondary>
        </div>
      )}
      <div ref={btnWrapperIkkePersonnrRef}>
        <Button.Secondary
          disabled={loadingCsv}
          onClick={() => onClose(true, false)}
          data-cy={'download-assignment'}
        >
          {labels.lastNedStudenterPaaPraksisstedSomCsv}
        </Button.Secondary>
      </div>
      {confirmDownloadDialogOpen && (
        <ConfirmDownloadStudentsCsvDialog
          onClose={onCloseConfirmDownloadStudentsCsvDialog}
        />
      )}
    </>
  );
};

interface ConfirmDownloadStudentsCsvDialogProps {
  onClose: (confirmed: boolean) => void;
}

const ConfirmDownloadStudentsCsvDialog: React.FC<
  React.PropsWithChildren<ConfirmDownloadStudentsCsvDialogProps>
> = ({ onClose }) => {
  const { formatMessage } = useIntl();

  const labels = {
    submitLabel: formatMessage({
      id: translations.lastNedStudenterPaaPraksisstedSomCsv.id,
    }),
    title: formatMessage({
      id: translations.dialogConfirmDownloadStudentsCsvTitle.id,
    }),
    content: formatMessage({
      id: translations.dialogConfirmDownloadStudentsCsvContent.id,
    }),
    content2: formatMessage({
      id: translations.dialogConfirmDownloadStudentsCsvContent2.id,
    }),
  };

  return (
    <ConfirmationDialog
      title={labels.title}
      submitLabel={labels.submitLabel}
      onClose={onClose}
      isOpen
    >
      <p>{labels.content}</p>
      <p>{labels.content2}</p>
    </ConfirmationDialog>
  );
};

export default FordelingDownloadButtons;
